import React, { type SetStateAction, type Dispatch, type MouseEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import ShrinkVerticalIcon from '@atlaskit/icon/core/shrink-vertical';

import {
	handleFooterCommentsNavigation,
	handleCommentsPanelNavigation,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { ReplyButton } from '@confluence/inline-comments-common/entry-points/ReplyButton.ts';

import { ViewReplyOptions } from '../helpers/repliesHelpers.ts';

const messages = defineMessages({
	hideReplies: {
		id: 'comments-common.comments.replies.footer.hideReplies',
		defaultMessage: '{numOfVisibleReplies, plural, one {Hide reply} other {Hide replies}}',
		description: 'Label for the button to hide comment replies',
	},
});

type RepliesFooterActionsProps = {
	isHideRepliesToggleVisible: boolean;
	isReplyButtonVisible: boolean;
	numOfVisibleReplies: number;
	location: LocationValue;
	scrollToId: string;
	setReplyView: Dispatch<SetStateAction<ViewReplyOptions>>;
	isOffline: boolean;
	handleReplyButtonClick: (event: MouseEvent<HTMLButtonElement>) => void;
};

export const RepliesFooterActions = ({
	isHideRepliesToggleVisible,
	isReplyButtonVisible,
	numOfVisibleReplies,
	location,
	scrollToId,
	setReplyView,
	isOffline,
	handleReplyButtonClick,
}: RepliesFooterActionsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const handleHideRepliesToggleClick = async (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();

		if (location === LocationValue.FOOTER) {
			await handleFooterCommentsNavigation({
				inline: 'start',
				block: 'start',
				commentId: scrollToId,
				scrollBehavior: ScrollBehavior.SMOOTH,
				shouldScrollIfCommentIsInViewport: false,
				awaitScrollCompletion: true,
			});
		} else {
			await handleCommentsPanelNavigation({
				block: 'start',
				annotationId: scrollToId,
				context: NavigationContext.PANEL,
				scrollBehavior: ScrollBehavior.SMOOTH,
				shouldScrollIfCommentIsInViewport: false,
				awaitScrollCompletion: true,
			});
		}

		// Hide replies regardless of scroll completion status
		// The scroll may not complete in test environments, but we still want to hide replies
		setReplyView(ViewReplyOptions.HIDDEN);
	};

	return (
		<>
			{isReplyButtonVisible && (
				<ReplyButton handleReplyButtonClick={handleReplyButtonClick} isOffline={isOffline} />
			)}
			{isHideRepliesToggleVisible && (
				<Button
					appearance="subtle"
					iconBefore={ShrinkVerticalIcon}
					onClick={handleHideRepliesToggleClick}
				>
					{formatMessage(messages.hideReplies, { numOfVisibleReplies })}
				</Button>
			)}
		</>
	);
};
